import type {
    ComponentType,
    FunctionComponent,
    PropsWithChildren
} from "react";
import { useEffect, useRef } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { GASv3 } from "../../analytics/atl-analytics/gasv3";
import { useAnalyticsClient } from "../../analytics/wac-analytics/analytics-client";

type FeatureExposedResponse = {
    /**
     * Whether the feature exposed event has been triggered.
     */
    hasFired: boolean;
};

type FeatureExposedProps = {
    /**
     * Conditions that must be met for the feature exposed to be triggered.
     */
    conditions?: boolean[];
    /**
     * Additional metadata to include in the feature exposed event.
     */
    metadata?: Record<string, string | undefined>;
};

type WithFeatureExposedProps<T> = T & {
    /**
     * Options to use when triggering feature exposed.
     */
    featureExposed?: FeatureExposedProps;
};

/**
 * useFeatureExposed
 *
 * Hook that indicates a user is enrolled into a particular feature.
 * @param experimentKey name of the past experiment
 * @param group name of the group that this version of the code belongs to
 * @param conditions conditions that must be met for the feature exposed to be triggered
 * @param metadata additional metadata to include in the feature exposed event
 * @returns FeatureExposedResponse
 *
 * @link https://data-portal.internal.atlassian.com/analytics/registry/29804
 *
 * @example
 * const { hasFired } = useFeatureExposed("experiment-name", "group", { conditions: [isInView] });
 */
export function useFeatureExposed(
    experimentKey: string,
    group: "Control" | "Test" | string,
    { conditions, metadata }: FeatureExposedProps = {}
): FeatureExposedResponse {
    const fired = useRef(false);
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();

    useEffect(() => {
        if (fired.current || !analyticsClient) {
            return;
        }
        const featureExposurePreconditionsMet =
            !conditions ||
            conditions.filter((condition) => !condition).length === 0;
        if (!featureExposurePreconditionsMet) {
            return;
        }
        // fire manual exposure event.
        createAnalyticsEvent({
            source: experimentKey,
            actionSubject: "feature",
            action: "exposed",
            actionSubjectId: experimentKey,
            attributes: GASv3.enrichEventData({
                group,
                ...metadata
            }),
            eventType: "track"
        }).fire();

        fired.current = true;
    }, [
        experimentKey,
        group,
        conditions,
        metadata,
        createAnalyticsEvent,
        analyticsClient
    ]);

    return {
        hasFired: fired.current
    };
}

/**
 * FeatureExposed
 *
 * Component that can be used to manually wrap an element to indicate a user is enrolled into a particular experiment.
 *
 * @example
 * <FeatureExposed experimentKey="experiment-name" group="control">
 *     <Component />
 * </FeatureExposed>
 */
export const FeatureExposed = ({
    experimentKey,
    group,
    conditions,
    metadata,
    children
}: PropsWithChildren<
    {
        /**
         * key of the experiment / feature
         */
        experimentKey: string;
        /**
         * name of the group that this version of the code belongs to
         */
        group: string;
    } & FeatureExposedProps
>) => {
    useFeatureExposed(experimentKey, group, { conditions, metadata });
    return children;
};

/**
 * withAttribution
 *
 * @param InnerComponent actual component you want to track feature exposure for
 * @param experimentKey key of the experiment / feature
 * @param group name of the group that this version of the code belongs to
 * @param options default options to send with feature exposed event. Can be overridden by the component's props
 *
 * @example
 * const InnerComponent = () => <div />;
 * export const MyComponent = withFeatureExposed("experiment-name", "group", InnerComponent);
 */
export function withFeatureExposed<TProps>(
    experimentKey: string,
    group: string,
    InnerComponent: ComponentType<WithFeatureExposedProps<TProps>>,
    options?: FeatureExposedProps
): FunctionComponent<WithFeatureExposedProps<TProps>> {
    return ({ featureExposed, ...props }: WithFeatureExposedProps<TProps>) => {
        useFeatureExposed(experimentKey, group, {
            ...options,
            ...featureExposed
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return <InnerComponent {...(props as any)} />;
    };
}
